import React, { useMemo } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils'; // Assuming this utility exists, if not I'll define it or use standard classes

/**
 * Parses raw HTML string and groups it by headers (H1-H6).
 * This allows us to create an accordion from flat HTML content.
 */
const parseHtmlToSections = (html) => {
  if (!html) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const sections = [];
  let currentSection = null;

  // We consider headers (H1-H6) and bold paragraphs as section titles
  const isHeader = (node) => {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
    const tagName = node.tagName.toLowerCase();
    
    // Standard headers
    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) return true;
    
    // Special case: bold text in a paragraph (common in rich text editors)
    if (tagName === 'p') {
      const children = Array.from(node.childNodes).filter(n => n.nodeType !== Node.TEXT_NODE || n.textContent.trim() !== '');
      if (children.length === 1 && (children[0].tagName?.toLowerCase() === 'strong' || children[0].tagName?.toLowerCase() === 'b' || children[0].tagName?.toLowerCase() === 'u')) {
        return true;
      }
    }
    return false;
  };

  const nodes = Array.from(doc.body.childNodes);

  nodes.forEach((node) => {
    if (isHeader(node)) {
      // Start a new section
      currentSection = {
        title: node.innerHTML,
        content: [],
        id: `section-${sections.length}`
      };
      sections.push(currentSection);
    } else if (currentSection) {
      // Add node to current section (excluding empty text nodes)
      if (node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '')) {
        currentSection.content.push(node.outerHTML || node.textContent);
      }
    } else {
      // Content before any header gets its own "Overview" section
      if (node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '')) {
        currentSection = {
          title: 'Product Overview',
          content: [node.outerHTML || node.textContent],
          id: 'section-overview'
        };
        sections.push(currentSection);
      }
    }
  });

  return sections.map(s => ({
    ...s,
    contentHtml: s.content.join('')
  }));
};

export function CollapsibleDescription({ html, className }) {
  const sections = useMemo(() => parseHtmlToSections(html), [html]);

  if (!sections.length) return null;

  return (
    <Accordion.Root
      type="single"
      defaultValue={sections[0]?.id} 
      collapsible
      className={cn("w-full space-y-4", className)}
    >
      {sections.map((section) => (
        <Accordion.Item
          key={section.id}
          value={section.id}
          className="group border border-gray-100 rounded-3xl bg-white overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
        >
          <Accordion.Header className="flex">
            <Accordion.Trigger className="flex flex-1 items-center justify-between px-6 py-5 text-left font-heading text-lg text-secondary hover:bg-surface/50 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">
              <span dangerouslySetInnerHTML={{ __html: section.title }} />
              <ChevronDown className="h-5 h-5 text-muted-foreground transition-transform duration-300 group-data-[state=open]:rotate-180" />
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content className="data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown overflow-hidden text-sm">
            <div 
              className="px-6 pb-6 pt-2 prose-custom max-w-none prose-table:m-0 prose-td:py-3"
              dangerouslySetInnerHTML={{ __html: section.contentHtml }}
            />
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
}
